// Local convenience cache for a bearer link. The server remains authoritative:
// every request re-validates the token, so this is never an access grant itself.
import type { ResourceKind } from "@checklist/shared";

const SESSIONS_KEY = "checklist.shareSessions";
const GUEST_ID_KEY = "checklist.guestId";
const GUEST_NAME_KEY = "checklist.guestName";

function key(kind: ResourceKind, id: string): string {
  return `${kind}:${id}`;
}

function read(): Record<string, string> {
  try {
    const value = JSON.parse(localStorage.getItem(SESSIONS_KEY) ?? "{}") as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] =>
      typeof entry[1] === "string" && entry[1].length > 0
    ));
  } catch {
    return {};
  }
}

function write(value: Record<string, string>): void {
  try { localStorage.setItem(SESSIONS_KEY, JSON.stringify(value)); } catch { /* private mode */ }
}

export function getShareSession(kind: ResourceKind, id: string): string | null {
  return read()[key(kind, id)] ?? null;
}

export function setShareSession(kind: ResourceKind, id: string, token: string): void {
  const sessions = read();
  sessions[key(kind, id)] = token;
  write(sessions);
}

export function clearShareSession(kind: ResourceKind, id: string): void {
  const sessions = read();
  delete sessions[key(kind, id)];
  write(sessions);
}

export interface GuestIdentity { guestId: string; displayName: string | null }

export function getGuestIdentity(): GuestIdentity {
  let guestId: string | null = null;
  try { guestId = localStorage.getItem(GUEST_ID_KEY); } catch { /* ignore */ }
  if (!guestId || !/^[A-Za-z0-9_-]{22}$/.test(guestId)) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const encoded = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
    try { localStorage.setItem(GUEST_ID_KEY, encoded); } catch { /* ignore */ }
    guestId = encoded;
  }
  let displayName: string | null = null;
  try { displayName = localStorage.getItem(GUEST_NAME_KEY); } catch { /* ignore */ }
  return { guestId, displayName: displayName?.trim() || null };
}

export function setGuestName(name: string): void {
  const cleaned = name.trim().slice(0, 40) || "Guest";
  try { localStorage.setItem(GUEST_NAME_KEY, cleaned); } catch { /* ignore */ }
}
